(() => {
  'use strict';

  if (window.MijnDozieStorefrontLoaded) return;
  window.MijnDozieStorefrontLoaded = true;

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[×✕]/g, 'x')
      .replace(/(\d)\s*x\s*(\d)/g, '$1x$2')
      .replace(/(\d)\s*x\s*(\d)/g, '$1x$2')
      .replace(/\s+/g, ' ')
      .trim();

  const slugify = (value) =>
    normalize(value)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  const formatUnit = (unit) => {
    const labels = {
      pieces: 'stuks',
      piece: 'stuk',
      packs: 'verpakkingen',
      pack: 'verpakking',
      kg: 'kg',
      pallets: 'pallets',
      pallet: 'pallet',
      on_request: 'Op aanvraag',
    };
    return labels[unit] || unit || 'stuks';
  };

  const formatLeadTime = (item) => {
    const min = Number(item.lead_time_min_days);
    const max = Number(item.lead_time_max_days);
    const hasMin = item.lead_time_min_days !== null && item.lead_time_min_days !== undefined && item.lead_time_min_days !== '' && Number.isFinite(min);
    const hasMax = item.lead_time_max_days !== null && item.lead_time_max_days !== undefined && item.lead_time_max_days !== '' && Number.isFinite(max);
    if (hasMin && hasMax) return min === max ? `${min} werkdagen` : `${min}–${max} werkdagen`;
    if (hasMin) return `Vanaf ${min} werkdagen`;
    return 'Op aanvraag';
  };

  const create = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = text;
    return element;
  };

  const getQuery = () => new URLSearchParams(window.location.search);

  const setQuery = (updates) => {
    const url = new URL(window.location.href);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '' || value === false) {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, String(value));
      }
    });
    window.history.replaceState({}, '', url);
  };

  const syncScrollLock = () => {
    const overlayOpen = document.querySelector(
      '[data-md-brand-header][data-menu-open="true"], [data-md-catalog][data-filter-open="true"]',
    );
    document.documentElement.classList.toggle('md-scroll-locked', Boolean(overlayOpen));
  };

  const getFocusable = (container) =>
    [...container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')]
      .filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0);

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab') return;
    const focusable = getFocusable(container);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  function initNavigation(root = document) {
    root.querySelectorAll('[data-md-brand-header]').forEach((header) => {
      if (header.dataset.initialized === 'true') return;
      header.dataset.initialized = 'true';
      const toggle = header.querySelector('[data-md-menu-toggle]');
      const nav = header.querySelector('[data-md-navigation]');
      const scrim = header.querySelector('[data-md-menu-scrim]');
      const headerSearch = header.querySelector('[data-md-header-search]');
      if (headerSearch && !headerSearch.value) headerSearch.value = getQuery().get('q') || '';
      if (!toggle || !nav) return;

      const positionMobileNav = () => {
        const headerBottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        header.style.setProperty('--md-mobile-nav-top', `${headerBottom}px`);
      };

      const setOpen = (open) => {
        if (open) positionMobileNav();
        header.dataset.menuOpen = String(open);
        toggle.setAttribute('aria-expanded', String(open));
        if (!open) nav.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
        syncScrollLock();
        if (open) nav.querySelector('summary, a')?.focus();
      };

      toggle.addEventListener('click', () => setOpen(header.dataset.menuOpen !== 'true'));
      scrim?.addEventListener('click', () => {
        setOpen(false);
        toggle.focus();
      });
      header.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && header.dataset.menuOpen === 'true') {
          setOpen(false);
          toggle.focus();
          return;
        }
        if (header.dataset.menuOpen === 'true' && window.matchMedia('(max-width: 989px)').matches) {
          trapFocus(event, header);
        }
      });
      nav.addEventListener('click', (event) => {
        if (event.target.closest('a')) setOpen(false);
      });

      const megaMenus = [...nav.querySelectorAll('details')];
      megaMenus.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          megaMenus.filter((candidate) => candidate !== details).forEach((candidate) => {
            candidate.open = false;
          });
        });
      });

      document.addEventListener('click', (event) => {
        if (!header.contains(event.target)) {
          megaMenus.forEach((details) => {
            details.open = false;
          });
        }
      });

      const current = new URL(window.location.href);
      const links = [...nav.querySelectorAll('a[href]')];
      const matches = links.filter((link) => {
        const target = new URL(link.href, window.location.origin);
        return target.pathname === current.pathname && target.search === current.search;
      });
      const activeLink = matches[0];
      activeLink?.setAttribute('aria-current', 'page');
      if (current.pathname === '/pages/assortiment') {
        nav.querySelector('.md-mega--assortment > details > summary')?.setAttribute('aria-current', 'page');
      }
      if (current.pathname.startsWith('/pages/sector')) {
        nav.querySelector('.md-mega--sectors > details > summary')?.setAttribute('aria-current', 'page');
      }

      window.addEventListener('resize', () => {
        if (window.matchMedia('(min-width: 990px)').matches && header.dataset.menuOpen === 'true') {
          setOpen(false);
        } else if (header.dataset.menuOpen === 'true') {
          positionMobileNav();
        }
      });
    });
  }

  function buildBadge(label, modifier) {
    return create('li', `md-badge${modifier ? ` md-badge--${modifier}` : ''}`, label);
  }

  function appendBadges(list, item) {
    const route = item.route || 'standaard';
    const routeLabels = {
      snel: ['Snel leverbaar', 'fast'],
      lage_moq: ['Lage MOQ', 'low'],
      volume: ['Grote oplage', 'volume'],
      offerte: ['Op aanvraag', 'quote'],
    };
    if (routeLabels[route]) list.append(buildBadge(...routeLabels[route]));
    if (item.customizable) list.append(buildBadge('Te bedrukken', 'print'));
    (Array.isArray(item.certifications) ? item.certifications : [])
      .slice(0, 2)
      .filter(Boolean)
      .forEach((certification) => list.append(buildBadge(certification, 'certified')));
  }

  function buildFixtureCard(item, catalogUrl, isB2b, hasCustomer = false) {
    const article = create('article', 'md-catalog-card');
    article.dataset.mdCatalogItem = '';
    article.dataset.search = normalize(
      [item.public_sku, item.title_nl, item.dimensions, item.capacity, item.material].join(' '),
    );
    article.dataset.route = item.route || 'standaard';
    article.dataset.category = slugify(item.category);
    article.dataset.producttype = slugify(item.subcategory);
    article.dataset.material = slugify(item.material);
    article.dataset.dimensions = normalize([item.dimensions, item.capacity].filter(Boolean).join(' '));
    article.dataset.printMethods = (item.print_methods || []).map(slugify).join(',');
    article.dataset.sustainable = String((item.certifications || []).length > 0);
    article.dataset.customizable = String(Boolean(item.customizable));
    article.dataset.moq = item.public_moq_value == null ? '' : String(item.public_moq_value);
    article.dataset.moqUnit = item.public_moq_unit || 'on_request';
    article.dataset.lead = item.lead_time_max_days == null ? '' : String(item.lead_time_max_days);
    article.dataset.title = normalize(item.title_nl);
    article.dataset.canonicalId = item.canonical_id || '';
    article.dataset.publicSku = item.public_sku || '';

    const detailUrl = new URL(catalogUrl, window.location.origin);
    const currentUrl = new URL(window.location.href);
    if (currentUrl.pathname === detailUrl.pathname) {
      currentUrl.searchParams.forEach((value, key) => {
        if (key !== 'item') detailUrl.searchParams.set(key, value);
      });
    }
    detailUrl.searchParams.set('item', item.slug);
    const mediaLink = create('a', 'md-catalog-card__media');
    mediaLink.href = detailUrl.toString();
    mediaLink.setAttribute('aria-label', `Bekijk ${item.title_nl}`);

    const media = Array.isArray(item.media) ? item.media[0] : null;
    if (media?.url) {
      const image = create('img', 'md-catalog-card__image');
      image.src = media.url;
      image.alt =
        media.alt ||
        [item.title_nl, [item.material, item.dimensions].filter(Boolean).join(', ')]
          .filter(Boolean)
          .join(' – ');
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 900;
      image.height = 675;
      mediaLink.append(image);
    } else {
      mediaLink.append(create('span', 'md-catalog-card__media-empty', 'Productbeeld volgt'));
    }

    const content = create('div', 'md-catalog-card__content');
    const badges = create('ul', 'md-badges');
    badges.setAttribute('aria-label', 'Productkenmerken');
    appendBadges(badges, item);

    const title = create('h3', 'md-catalog-card__title');
    const titleLink = create('a', '', item.title_nl || 'Naam volgt');
    titleLink.href = detailUrl.toString();
    title.append(titleLink);
    const skuText = item.public_sku
      ? create('p', 'md-catalog-card__sku', `Artikelnummer ${item.public_sku}`)
      : null;

    const specs = [item.material, item.dimensions, item.capacity].filter(Boolean).join(' · ');
    const specText = specs ? create('p', 'md-catalog-card__specs', specs) : null;

    const facts = create('dl', 'md-catalog-card__facts');
    const unit = formatUnit(item.public_moq_unit);
    const hasMoq = item.public_moq_value != null && item.public_moq_unit !== 'on_request';
    const hasIncrement = item.order_increment != null && item.public_moq_unit !== 'on_request';
    const directOfferReady =
      hasMoq &&
      hasIncrement &&
      item.lead_time_min_days != null &&
      item.lead_time_max_days != null &&
      item.order_mode === 'direct' &&
      item.route !== 'offerte' &&
      Number.isInteger(item.cart_quantity_min) &&
      item.cart_quantity_min > 0 &&
      Number.isInteger(item.cart_quantity_increment) &&
      item.cart_quantity_increment > 0;
    const moqFact = create('div');
    moqFact.append(
      create('dt', '', 'Minimale afname'),
      create(
        'dd',
        '',
        hasMoq
          ? `${item.public_moq_value} ${unit}`
          : 'Op aanvraag',
      ),
    );
    const incrementFact = create('div');
    incrementFact.append(
      create('dt', '', 'Besteleenheid'),
      create('dd', '', hasIncrement ? `${item.order_increment} ${unit}` : 'Op aanvraag'),
    );
    const leadFact = create('div');
    leadFact.append(create('dt', '', 'Verwachte levertijd'), create('dd', '', formatLeadTime(item)));
    facts.append(moqFact, incrementFact);
    if (item.units_per_pack != null) {
      const packFact = create('div');
      packFact.append(
        create('dt', '', 'Inhoud verpakking'),
        create('dd', '', `${item.units_per_pack} stuks`),
      );
      facts.append(packFact);
    }
    facts.append(leadFact);

    const footer = create('div', 'md-catalog-card__footer');
    const priceState = create(
      'span',
      isB2b || hasCustomer ? 'md-catalog-card__access-state' : 'md-catalog-card__locked',
    );
    if (isB2b) {
      priceState.textContent = directOfferReady
        ? 'Zakelijke prijs wordt gecontroleerd'
        : 'Prijs en planning op aanvraag';
    } else if (hasCustomer) {
      priceState.textContent = 'Zakelijke toegang nog niet actief';
    } else {
      const lock = create('span', 'md-catalog-card__lock-icon', 'B2B');
      lock.setAttribute('aria-hidden', 'true');
      priceState.append(lock, create('span', '', 'Prijs zichtbaar na goedkeuring'));
    }
    const detailLink = create(
      'a',
      'md-button md-button--secondary md-button--small',
      'Bekijk product',
    );
    detailLink.href = detailUrl.toString();
    footer.append(priceState, detailLink);

    content.append(badges, title);
    if (skuText) content.append(skuText);
    if (specText) content.append(specText);
    content.append(facts, footer);
    article.append(mediaLink, content);
    return article;
  }

  function buildDetailFact(term, description) {
    const wrapper = create('div');
    wrapper.append(create('dt', '', term), create('dd', '', description || 'Na afstemming'));
    return wrapper;
  }

  function renderFixtureDetail(catalog, item, allItems = []) {
    const detail = catalog.querySelector('[data-md-catalog-detail]');
    const listView = catalog.querySelector('[data-md-catalog-list-view]');
    if (!detail || !listView) return;
    listView.hidden = true;
    detail.hidden = false;
    detail.replaceChildren();

    const resultsUrl = new URL(window.location.href);
    resultsUrl.searchParams.delete('item');
    const back = create('a', 'md-text-link md-product__back', '← Terug naar de resultaten');
    back.href = `${resultsUrl.pathname}${resultsUrl.search}`;

    const breadcrumbs = create('nav', 'md-breadcrumbs');
    breadcrumbs.setAttribute('aria-label', 'Kruimelpad');
    const homeLink = create('a', '', 'Home');
    homeLink.href = '/';
    const assortmentLink = create('a', '', 'Assortiment');
    assortmentLink.href = catalog.dataset.catalogUrl || '/pages/assortiment';
    const currentCrumb = create('span', '', item.title_nl || 'Product');
    currentCrumb.setAttribute('aria-current', 'page');
    const separatorOne = create('span', '', '/');
    separatorOne.setAttribute('aria-hidden', 'true');
    const separatorTwo = create('span', '', '/');
    separatorTwo.setAttribute('aria-hidden', 'true');
    breadcrumbs.append(homeLink, separatorOne, assortmentLink, separatorTwo, currentCrumb);

    const grid = create('div', 'md-product__grid');
    const mediaWrap = create('div', 'md-product__media');
    const media = Array.isArray(item.media) ? item.media[0] : null;
    if (media?.url) {
      const image = create('img');
      image.src = media.url;
      image.alt = media.alt || item.title_nl || '';
      image.decoding = 'async';
      image.width = 1200;
      image.height = 1200;
      mediaWrap.append(image);
    } else {
      mediaWrap.append(create('div', 'md-product__media-empty', 'Productbeeld volgt'));
    }

    const info = create('div', 'md-product__info');
    const badges = create('ul', 'md-badges');
    badges.setAttribute('aria-label', 'Productkenmerken');
    appendBadges(badges, item);
    const title = create('h1', 'md-product__title', item.title_nl || 'Product');
    title.id = `MdFixtureTitle-${slugify(item.slug || item.canonical_id || 'product')}`;
    title.tabIndex = -1;
    const description = create(
      'p',
      'md-product__description',
      item.description_nl || 'Productspecificaties en bedrukkingsmogelijkheden worden gecontroleerd.',
    );
    const facts = create('dl', 'md-product__facts');
    facts.append(
      buildDetailFact('Artikelnummer', item.public_sku || 'Op aanvraag'),
      buildDetailFact('Materiaal', item.material),
      buildDetailFact('Formaat', item.dimensions || item.capacity),
      buildDetailFact(
        'Minimale afname',
        item.public_moq_value != null && item.public_moq_unit !== 'on_request'
          ? `${item.public_moq_value} ${formatUnit(item.public_moq_unit)}`
          : 'Op aanvraag',
      ),
      buildDetailFact(
        'Besteleenheid',
        item.order_increment != null && item.public_moq_unit !== 'on_request'
          ? `${item.order_increment} ${formatUnit(item.public_moq_unit)}`
          : 'Op aanvraag',
      ),
      buildDetailFact(
        'Inhoud verpakking',
        item.units_per_pack != null ? `${item.units_per_pack} stuks` : 'Op aanvraag',
      ),
      buildDetailFact('Levertijd', formatLeadTime(item)),
      buildDetailFact(
        'Bedrukking',
        item.customizable
          ? (item.print_methods || []).join(', ') || 'Mogelijk'
          : 'Niet beschikbaar',
      ),
      buildDetailFact('Bestelroute', item.order_mode === 'quote' ? 'Offerte' : 'Direct of offerte'),
    );

    const isB2b = catalog.dataset.b2b === 'true';
    const hasCustomer = catalog.dataset.customer === 'true';
    const access = create('div', 'md-price-gate');
    const icon = create('div', 'md-price-gate__icon', 'B2B');
    icon.setAttribute('aria-hidden', 'true');
    const body = create('div', 'md-price-gate__body');
    const gateEyebrow = isB2b
      ? 'Zakelijke opties'
      : hasCustomer
        ? 'Zakelijke toegang'
        : 'Alleen voor zakelijke klanten';
    const gateTitle = isB2b
      ? 'Prijs en planning op aanvraag'
      : hasCustomer
        ? 'Je zakelijke toegang is nog niet actief'
        : 'Zakelijke prijs bekijken';
    const gateText = isB2b
      ? 'Dit openbare catalogusrecord bevat bewust geen prijs. Deel je wensen voor een gecontroleerde offerte.'
      : hasCustomer
        ? 'Je kunt alle specificaties blijven bekijken. Na goedkeuring worden beschikbare zakelijke prijzen en bestellen actief.'
        : 'Log in met een goedgekeurd zakelijk account om prijzen exclusief btw te bekijken en te bestellen.';
    body.append(
      create('p', 'md-eyebrow', gateEyebrow),
      create('h2', 'md-price-gate__title', gateTitle),
      create('p', '', gateText),
    );
    const actions = create('div', 'md-button-row');
    const primary = create('a', 'md-button md-button--primary');
    if (isB2b) {
      primary.textContent = 'Vraag een offerte aan';
      primary.href = `/pages/offerte-aanvragen?item=${encodeURIComponent(item.slug)}`;
      actions.append(primary);
    } else if (hasCustomer) {
      primary.textContent = 'Bekijk of voltooi je aanvraag';
      primary.href = '/pages/zakelijk-account-aanvragen';
      const secondary = create('a', 'md-button md-button--secondary', 'Vraag een offerte aan');
      secondary.href = `/pages/offerte-aanvragen?item=${encodeURIComponent(item.slug)}`;
      actions.append(primary, secondary);
    } else {
      primary.textContent = 'Inloggen';
      primary.href = '/account/login';
      const secondary = create('a', 'md-button md-button--secondary', 'Zakelijk account aanvragen');
      secondary.href = '/pages/zakelijk-account-aanvragen';
      actions.append(primary, secondary);
    }
    if (item.customizable) {
      const printing = create('a', 'md-text-link', 'Zo werkt bedrukken');
      printing.href = '/pages/bedrukken';
      actions.append(printing);
    }
    body.append(actions);
    access.append(icon, body);
    if (catalog.dataset.mascotUrl) {
      const mascotWrap = create('div', 'md-price-gate__mascot');
      mascotWrap.setAttribute('aria-hidden', 'true');
      const mascot = create('img');
      mascot.src = catalog.dataset.mascotUrl;
      mascot.alt = '';
      mascot.loading = 'lazy';
      mascot.width = 1300;
      mascot.height = 1179;
      mascotWrap.append(mascot);
      access.append(mascotWrap);
    }

    info.append(badges, title, description, facts, access);
    grid.append(mediaWrap, info);

    const detailsGrid = create('div', 'md-product__details-grid');
    const deliveryCard = create('article', 'md-content-card');
    deliveryCard.append(
      create('h2', '', 'Afname en levering'),
      create(
        'p',
        '',
        item.lead_time_min_days != null && item.lead_time_max_days != null
          ? `Verwachte levertijd: ${formatLeadTime(item)}. De definitieve planning wordt in de offerte bevestigd.`
          : 'De levertijd wordt na controle in de offerte bevestigd.',
      ),
      create('p', '', 'Pallet-, maatwerk- of nog onbekend transport wordt afzonderlijk gecontroleerd.'),
    );

    const printCard = create('article', 'md-content-card');
    printCard.append(
      create('h2', '', item.customizable ? 'Bedrukking en ontwerp' : 'Uitvoering en bestelroute'),
      create(
        'p',
        '',
        item.customizable
          ? `Beschikbare drukmethode: ${(item.print_methods || []).join(', ') || 'wordt gecontroleerd'}. De technische haalbaarheid hangt af van uitvoering en oplage.`
          : 'Voor dit catalogusrecord is geen bedrukoptie geregistreerd. Een andere uitvoering kan via een offerte worden gecontroleerd.',
      ),
    );
    const printLink = create('a', 'md-text-link', item.customizable ? 'Lees hoe bedrukken werkt' : 'Bespreek een andere uitvoering');
    printLink.href = item.customizable ? '/pages/bedrukken' : `/pages/offerte-aanvragen?item=${encodeURIComponent(item.slug)}`;
    printCard.append(printLink);

    const proofCard = create('article', 'md-content-card');
    proofCard.append(create('h2', '', item.customizable ? 'Eerst akkoord, dan productie' : 'Van aanvraag naar bevestiging'));
    const steps = create('ol', 'md-plain-step-list');
    const stepLabels = item.customizable
      ? ['Ontwerp ontvangen', 'Technische controle', 'Digitale proef', 'Jouw goedkeuring', 'Productie']
      : ['Wensen controleren', 'Prijs en planning bevestigen', 'Bestelling afronden', 'Levering'];
    stepLabels.forEach((label) => steps.append(create('li', '', label)));
    proofCard.append(
      steps,
      create(
        'p',
        '',
        item.customizable
          ? 'Productie start pas nadat je de digitale proef expliciet hebt goedgekeurd.'
          : 'Je ontvangt de definitieve voorwaarden voordat je de bestelling afrondt.',
      ),
    );
    detailsGrid.append(deliveryCard, printCard, proofCard);

    const faq = create('section', 'md-product__faq');
    const faqTitle = create('h2', 'md-heading md-heading--section', 'Veelgestelde vragen over dit artikel');
    const faqList = create('div', 'md-faq-list');
    const deliveryFaq = create('details');
    deliveryFaq.append(
      create('summary', '', 'Wanneer begint de verwachte levertijd?'),
      create(
        'p',
        '',
        item.customizable
          ? 'Bij bedrukt werk begint de verwachte productietijd na jouw goedkeuring van de digitale proef.'
          : 'Bij een onbedrukte uitvoering begint de verwachte levertijd na order- en beschikbaarheidsbevestiging.',
      ),
    );
    const sampleFaq = create('details');
    sampleFaq.append(
      create('summary', '', 'Kan ik eerst een sample aanvragen?'),
      create('p', '', 'Vermeld een sample in je offerteaanvraag. Beschikbaarheid en eventuele kosten worden per uitvoering gecontroleerd.'),
    );
    faqList.append(deliveryFaq, sampleFaq);
    faq.append(faqTitle, faqList);

    const relatedItems = allItems
      .filter((candidate) => candidate.slug !== item.slug && candidate.category === item.category)
      .sort((first, second) => Number((second.media || []).length > 0) - Number((first.media || []).length > 0))
      .slice(0, 3);
    const related = create('section', 'md-product-related');
    if (relatedItems.length) {
      const relatedHeading = create('div', 'md-section-heading');
      const relatedCopy = create('div');
      relatedCopy.append(
        create('p', 'md-eyebrow', 'Verder vergelijken'),
        create('h2', 'md-heading md-heading--section', 'Vergelijk soortgelijke verpakkingen'),
      );
      relatedHeading.append(relatedCopy);
      const relatedGrid = create('ul', 'md-catalog__grid md-catalog__grid--related');
      relatedItems.forEach((candidate) => {
        const wrapper = create('li', 'md-catalog__grid-item');
        wrapper.append(
          buildFixtureCard(
            candidate,
            catalog.dataset.catalogUrl || '/pages/assortiment',
            isB2b,
            hasCustomer,
          ),
        );
        relatedGrid.append(wrapper);
      });
      related.append(relatedHeading, relatedGrid);
    }

    detail.append(breadcrumbs, back, grid, detailsGrid, faq);
    if (relatedItems.length) detail.append(related);
    catalog.setAttribute('aria-labelledby', title.id);
    document.title = `${item.title_nl} – MijnDozie`;
    window.requestAnimationFrame(() => title.focus({ preventScroll: true }));
  }

  function initCatalog(root = document) {
    root.querySelectorAll('[data-md-catalog]').forEach(async (catalog) => {
      if (catalog.dataset.initialized === 'true') return;
      catalog.dataset.initialized = 'true';

      const grid = catalog.querySelector('[data-md-catalog-grid]');
      const count = catalog.querySelector('[data-md-catalog-count]');
      const search = catalog.querySelector('[data-md-catalog-search]');
      const sort = catalog.querySelector('[data-md-catalog-sort]');
      const resetButtons = [...catalog.querySelectorAll('[data-md-catalog-reset]')];
      const empty = catalog.querySelector('[data-md-catalog-empty]');
      const loading = catalog.querySelector('[data-md-catalog-loading]');
      const error = catalog.querySelector('[data-md-catalog-error]');
      const filterToggle = catalog.querySelector('[data-md-filter-toggle]');
      const filterClose = catalog.querySelector('[data-md-filter-close]');
      const filterScrim = catalog.querySelector('[data-md-filter-scrim]');
      const filterPanel = catalog.querySelector('.md-catalog__filters');
      const filterCount = catalog.querySelector('[data-md-filter-count]');
      const emptyTitle = catalog.querySelector('[data-md-empty-title]');
      const emptyText = catalog.querySelector('[data-md-empty-text]');
      let filters = [...catalog.querySelectorAll('[data-md-filter]')];
      const dimensionFilter = catalog.querySelector('[data-md-dimension-filter]');
      const maxMoqInput = catalog.querySelector('[data-md-max-moq]');
      const maxLeadInput = catalog.querySelector('[data-md-max-lead]');
      const fixtureUrl = catalog.dataset.fixtureUrl;
      let fixtures = [];

      const setFiltersOpen = (open) => {
        catalog.dataset.filterOpen = String(open);
        filterToggle?.setAttribute('aria-expanded', String(open));
        syncScrollLock();
        if (open && filterPanel) (getFocusable(filterPanel)[0] || filterPanel).focus();
      };
      filterToggle?.addEventListener('click', () => {
        const open = catalog.dataset.filterOpen !== 'true';
        setFiltersOpen(open);
      });
      filterClose?.addEventListener('click', () => {
        setFiltersOpen(false);
        filterToggle?.focus();
      });
      filterScrim?.addEventListener('click', () => {
        setFiltersOpen(false);
        filterToggle?.focus();
      });
      catalog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && catalog.dataset.filterOpen === 'true') {
          setFiltersOpen(false);
          filterToggle?.focus();
          return;
        }
        if (catalog.dataset.filterOpen === 'true' && filterPanel) trapFocus(event, filterPanel);
      });
      window.addEventListener('resize', () => {
        if (window.matchMedia('(min-width: 990px)').matches && catalog.dataset.filterOpen === 'true') {
          setFiltersOpen(false);
        }
      });

      const query = getQuery();
      const hydrateFromQuery = () => {
        if (search) search.value = query.get('q') || '';
        if (sort && query.get('sort')) sort.value = query.get('sort');
        if (dimensionFilter) dimensionFilter.value = query.get('afmeting') || '';
        if (maxMoqInput) maxMoqInput.value = query.get('max_moq') || '';
        if (maxLeadInput) maxLeadInput.value = query.get('max_levertijd') || '';
        filters.forEach((input) => {
          const value = query.get(input.dataset.mdFilter);
          if (input.type === 'checkbox') input.checked = value?.split(',').includes(input.value) || false;
          else if (value !== null) input.value = value;
        });
      };
      hydrateFromQuery();

      const getCards = () => [...grid.querySelectorAll('[data-md-catalog-item]')];
      const selectedValues = (name) =>
        filters
          .filter((input) => input.dataset.mdFilter === name && input.checked)
          .map((input) => input.value);

      const syncUrl = () => {
        const updates = {
          q: search?.value.trim() || '',
          sort: sort?.value && sort.value !== 'featured' ? sort.value : '',
          afmeting: dimensionFilter?.value.trim() || '',
          max_moq: maxMoqInput?.value || '',
          max_levertijd: maxLeadInput?.value || '',
        };
        [...new Set(filters.map((input) => input.dataset.mdFilter))].forEach((name) => {
          const values = selectedValues(name);
          updates[name] = values.length ? values.join(',') : '';
        });
        setQuery(updates);
      };

      const updateRouteChips = () => {
        const currentUrl = new URL(window.location.href);
        catalog.querySelectorAll('.md-route-chips a[href]').forEach((link) => {
          const target = new URL(link.href, window.location.origin);
          const active = [...target.searchParams.entries()].every(
            ([key, value]) => currentUrl.searchParams.get(key) === value,
          );
          if (active && target.searchParams.size > 0) link.setAttribute('aria-current', 'page');
          else link.removeAttribute('aria-current');
        });
      };

      const apply = () => {
        const term = normalize(search?.value);
        const selectedRoutes = selectedValues('route');
        const selectedCategories = selectedValues('categorie');
        const selectedProductTypes = selectedValues('producttype');
        const selectedMaterials = selectedValues('materiaal');
        const selectedPrintMethods = selectedValues('drukmethode');
        const selectedMoqUnits = selectedValues('moq_eenheid');
        const sustainable = selectedValues('duurzaam');
        const customizable = selectedValues('bedrukbaar');
        const dimensionTerm = normalize(dimensionFilter?.value);
        const maxMoq = Number(maxMoqInput?.value || 0);
        const maxLead = Number(maxLeadInput?.value || 0);
        const cards = getCards();
        let visible = 0;

        cards.forEach((card, index) => {
          const matches =
            (!term || normalize(card.dataset.search).includes(term)) &&
            (!selectedRoutes.length || selectedRoutes.includes(card.dataset.route)) &&
            (!selectedCategories.length || selectedCategories.includes(card.dataset.category)) &&
            (!selectedProductTypes.length || selectedProductTypes.includes(card.dataset.producttype)) &&
            (!selectedMaterials.length || selectedMaterials.includes(card.dataset.material)) &&
            (!selectedPrintMethods.length ||
              selectedPrintMethods.some((method) => (card.dataset.printMethods || '').split(',').includes(method))) &&
            (!selectedMoqUnits.length || selectedMoqUnits.includes(card.dataset.moqUnit)) &&
            (!sustainable.length || sustainable.includes(card.dataset.sustainable)) &&
            (!customizable.length || customizable.includes(card.dataset.customizable)) &&
            (!dimensionTerm || normalize(card.dataset.dimensions).includes(dimensionTerm)) &&
            (!maxMoq || (card.dataset.moqUnit === 'pieces' && card.dataset.moq !== '' && Number(card.dataset.moq) <= maxMoq)) &&
            (!maxLead || (card.dataset.lead !== '' && Number(card.dataset.lead) <= maxLead));
          const wrapper = card.closest('.md-catalog__grid-item') || card;
          if (wrapper.dataset.mdOriginalIndex === undefined) wrapper.dataset.mdOriginalIndex = String(index);
          wrapper.hidden = !matches;
          if (matches) visible += 1;
        });

        const direction = sort?.value || 'featured';
        const wrappers = cards.map((card) => card.closest('.md-catalog__grid-item') || card);
        wrappers.sort((a, b) => {
          if (direction === 'featured') {
            return Number(a.dataset.mdOriginalIndex || 0) - Number(b.dataset.mdOriginalIndex || 0);
          }
            const first = a.querySelector?.('[data-md-catalog-item]') || a;
            const second = b.querySelector?.('[data-md-catalog-item]') || b;
            if (direction === 'moq_asc') {
              const unitOrder = ['pieces', 'packs', 'kg', 'pallets', 'on_request'];
              const unitDifference = unitOrder.indexOf(first.dataset.moqUnit) - unitOrder.indexOf(second.dataset.moqUnit);
              if (unitDifference !== 0) return unitDifference;
              const firstMoq = first.dataset.moq === '' ? Number.POSITIVE_INFINITY : Number(first.dataset.moq);
              const secondMoq = second.dataset.moq === '' ? Number.POSITIVE_INFINITY : Number(second.dataset.moq);
              return firstMoq - secondMoq;
            }
            if (direction === 'lead_asc') {
              const firstLead = first.dataset.lead === '' ? Number.POSITIVE_INFINITY : Number(first.dataset.lead);
              const secondLead = second.dataset.lead === '' ? Number.POSITIVE_INFINITY : Number(second.dataset.lead);
              return firstLead - secondLead;
            }
            return (first.dataset.title || '').localeCompare(second.dataset.title || '', 'nl');
        });
        wrappers.forEach((wrapper) => grid.append(wrapper));

        if (count) count.textContent = `${visible} ${visible === 1 ? 'artikel' : 'artikelen'}`;
        if (empty) empty.hidden = visible !== 0;
        const activeFilterCount =
          filters.filter((input) => input.checked).length +
          Number(Boolean(search?.value.trim())) +
          Number(Boolean(dimensionFilter?.value.trim())) +
          Number(Boolean(maxMoqInput?.value)) +
          Number(Boolean(maxLeadInput?.value));
        if (filterCount) {
          filterCount.textContent = String(activeFilterCount);
          filterCount.hidden = activeFilterCount === 0;
        }
        if (emptyTitle && emptyText) {
          const evidenceOnly = selectedValues('duurzaam').includes('true');
          emptyTitle.textContent = evidenceOnly
            ? 'Nog geen artikelen met gecontroleerd bewijskenmerk'
            : 'Geen verpakkingen gevonden';
          emptyText.textContent = evidenceOnly
            ? 'Duurzaamheidskenmerken worden pas getoond wanneer het bewijs aan het artikel is gekoppeld. Vraag ons om een gecontroleerde optie voor jouw toepassing.'
            : 'Pas één of meer filters aan, of laat ons meedenken over formaat, oplage en planning.';
        }
        syncUrl();
        updateRouteChips();
      };

      const selectPreviewItems = (items, limit) => {
        if (!limit || items.length <= limit) return items.slice(0, limit || items.length);
        const selected = [];
        const add = (candidate) => {
          if (candidate && !selected.includes(candidate) && selected.length < limit) selected.push(candidate);
        };
        const imageFirst = [...items].sort(
          (first, second) => Number((second.media || []).length > 0) - Number((first.media || []).length > 0),
        );
        [...new Set(items.map((item) => item.category).filter(Boolean))].forEach((category) => {
          add(imageFirst.find((item) => item.category === category));
        });
        [...new Set(items.map((item) => item.route).filter(Boolean))].forEach((route) => {
          add(imageFirst.find((item) => item.route === route));
        });
        imageFirst.forEach(add);
        return selected;
      };

      const renderFixtures = (items) => {
        if (!grid || !Array.isArray(items)) return;
        grid.replaceChildren();
        const limit = Number(catalog.dataset.limit || 0);
        selectPreviewItems(items, limit).forEach((item, index) => {
          const wrapper = create('li', 'md-catalog__grid-item');
          wrapper.dataset.mdOriginalIndex = String(index);
          wrapper.append(
            buildFixtureCard(
              item,
              catalog.dataset.catalogUrl || '/pages/assortiment',
              catalog.dataset.b2b === 'true',
              catalog.dataset.customer === 'true',
            ),
          );
          grid.append(wrapper);
        });
      };

      const populateDynamicFilters = (items) => {
        const configurations = [
          {
            key: 'producttype',
            values: items.map((item) => item.subcategory).filter(Boolean),
          },
          {
            key: 'materiaal',
            values: items.map((item) => item.material).filter(Boolean),
          },
          {
            key: 'drukmethode',
            values: items.flatMap((item) => item.print_methods || []).filter(Boolean),
          },
        ];
        configurations.forEach(({ key, values }) => {
          const container = catalog.querySelector(`[data-md-filter-options="${key}"]`);
          if (!container) return;
          const unique = [...new Set(values)].sort((a, b) => a.localeCompare(b, 'nl'));
          const fragment = document.createDocumentFragment();
          unique.forEach((label) => {
            const option = create('label', 'md-filter-option');
            const input = create('input');
            input.type = 'checkbox';
            input.value = slugify(label);
            input.dataset.mdFilter = key;
            option.append(input, document.createTextNode(` ${label}`));
            fragment.append(option);
          });
          container.replaceChildren(fragment);
        });
        filters = [...catalog.querySelectorAll('[data-md-filter]')];
        hydrateFromQuery();
      };

      if (fixtureUrl) {
        try {
          const response = await fetch(fixtureUrl, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const payload = await response.json();
          fixtures = Array.isArray(payload) ? payload : payload.items || [];
          const assetMapNode = document.querySelector('[data-md-catalog-asset-map]');
          if (assetMapNode) {
            try {
              const assetMap = JSON.parse(assetMapNode.textContent);
              fixtures = fixtures.map((item) => {
                if ((item.media || []).length || !assetMap[item.canonical_id]) return item;
                return { ...item, media: [assetMap[item.canonical_id]] };
              });
            } catch (assetMapError) {
              // The catalog remains usable without imagery; never fall back to unresolved asset:// URLs.
            }
          }
          populateDynamicFilters(fixtures);
          renderFixtures(fixtures);
          if (loading) loading.hidden = true;
        } catch (fetchError) {
          if (loading) loading.hidden = true;
          if (!getCards().length && error) {
            error.hidden = false;
            error.textContent = 'Het assortiment kon niet worden geladen. Probeer het later opnieuw.';
          }
        }
      } else if (loading) {
        loading.hidden = true;
      }

      const selectedSlug = getQuery().get('item');
      if (selectedSlug && fixtures.length) {
        const item = fixtures.find((candidate) => candidate.slug === selectedSlug);
        if (item) {
          renderFixtureDetail(catalog, item, fixtures);
          return;
        }
        if (error) {
          error.hidden = false;
          error.textContent = 'Dit catalogusitem bestaat niet of is niet meer beschikbaar.';
        }
      }

      search?.addEventListener('input', apply);
      sort?.addEventListener('change', apply);
      dimensionFilter?.addEventListener('input', apply);
      maxMoqInput?.addEventListener('change', apply);
      maxLeadInput?.addEventListener('change', apply);
      filters.forEach((input) => input.addEventListener('change', apply));
      const resetFilters = () => {
        if (search) search.value = '';
        if (sort) sort.value = 'featured';
        if (dimensionFilter) dimensionFilter.value = '';
        if (maxMoqInput) maxMoqInput.value = '';
        if (maxLeadInput) maxLeadInput.value = '';
        filters.forEach((input) => {
          if (input.type === 'checkbox') input.checked = false;
          else input.value = '';
        });
        apply();
      };
      resetButtons.forEach((button) => button.addEventListener('click', resetFilters));
      apply();
    });
  }

  function initCustomizer(root = document) {
    root.querySelectorAll('[data-md-customizer]').forEach((customizer) => {
      if (customizer.dataset.initialized === 'true') return;
      customizer.dataset.initialized = 'true';

      const steps = [...customizer.querySelectorAll('[data-md-step]')];
      const progress = [...customizer.querySelectorAll('[data-md-progress-step]')];
      const previous = customizer.querySelector('[data-md-previous]');
      const next = customizer.querySelector('[data-md-next]');
      const finish = customizer.querySelector('[data-md-finish]');
      const quantity = customizer.querySelector('[data-md-quantity]');
      const file = customizer.querySelector('[data-md-artwork]');
      const fileStatus = customizer.querySelector('[data-md-artwork-status]');
      let current = 0;

      const selectedText = (name, fallback) => {
        const field = customizer.querySelector(`[name="${name}"]:checked, [name="${name}"] option:checked`);
        return field?.dataset.label || field?.textContent?.trim() || field?.value || fallback;
      };

      const updateSummary = () => {
        const quantityUnit = quantity?.dataset.mdQuantityUnit || '';
        const values = {
          product: selectedText('md_product_choice', 'Nog kiezen'),
          print: selectedText('properties[Bedrukking]', 'Nog kiezen'),
          quantity: quantity?.value
            ? `${quantity.value}${quantityUnit ? ` ${quantityUnit}` : ''}`
            : 'Nog kiezen',
          artwork: file?.files?.[0]?.name || selectedText('ontwerpkeuze', 'Nog kiezen'),
        };
        Object.entries(values).forEach(([key, value]) => {
          const target = customizer.querySelector(`[data-md-summary="${key}"]`);
          if (target) target.textContent = value;
        });
      };

      const validateStep = () => {
        const required = [...steps[current].querySelectorAll('[required]')];
        for (const field of required) {
          if (!field.checkValidity()) {
            field.reportValidity();
            return false;
          }
        }
        return true;
      };

      const showStep = (index, moveFocus = true) => {
        current = Math.max(0, Math.min(index, steps.length - 1));
        steps.forEach((step, stepIndex) => {
          step.hidden = stepIndex !== current;
        });
        progress.forEach((item, stepIndex) => {
          if (stepIndex === current) item.setAttribute('aria-current', 'step');
          else item.removeAttribute('aria-current');
          item.dataset.complete = String(stepIndex < current);
        });
        if (previous) previous.hidden = current === 0;
        if (next) next.hidden = current === steps.length - 1;
        if (finish) finish.hidden = current !== steps.length - 1;
        if (moveFocus) steps[current]?.querySelector('h3')?.focus({ preventScroll: true });
        updateSummary();
      };

      next?.addEventListener('click', () => {
        if (validateStep()) showStep(current + 1);
      });
      previous?.addEventListener('click', () => showStep(current - 1));

      quantity?.addEventListener('change', () => {
        if (!quantity.value) {
          updateSummary();
          return;
        }
        const quantityKnown = quantity.dataset.mdQuantityKnown === 'true';
        const min = Number(quantity.min || 1);
        const step = Number(quantity.step || 1);
        let value = Math.max(Number(quantity.value), min);
        if (quantityKnown) value = min + Math.ceil((value - min) / step) * step;
        else value = Math.ceil(value);
        quantity.value = String(value);
        updateSummary();
      });

      file?.addEventListener('change', () => {
        const selected = file.files?.[0];
        const maximum = Number(file.dataset.maxBytes || 104857600);
        if (selected && selected.size > maximum) {
          file.value = '';
          if (fileStatus) fileStatus.textContent = 'Dit bestand is groter dan 100 MB. Kies een kleiner bestand.';
        } else if (fileStatus) {
          fileStatus.textContent = selected
            ? `${selected.name} is alleen lokaal geselecteerd en nog niet verstuurd. De definitieve aanleverstap volgt na bevestiging.`
            : 'Nog geen bestand geselecteerd.';
        }
        updateSummary();
      });

      customizer.addEventListener('change', updateSummary);
      showStep(0, false);
    });
  }

  function initApplicationForms(root = document) {
    root.querySelectorAll('[data-md-application-form]').forEach((form) => {
      if (form.dataset.initialized === 'true') return;
      form.dataset.initialized = 'true';
      const shippingSame = form.querySelector('[data-md-shipping-same]');
      const shippingFields = form.querySelector('[data-md-shipping-fields]');
      const syncShippingFields = () => {
        if (!shippingSame || !shippingFields) return;
        const usesBillingAddress = shippingSame.checked;
        shippingFields.hidden = usesBillingAddress;
        shippingFields.querySelectorAll('input, select, textarea').forEach((field) => {
          field.disabled = usesBillingAddress;
          field.required = !usesBillingAddress;
        });
      };
      shippingSame?.addEventListener('change', syncShippingFields);
      syncShippingFields();
      form.addEventListener('submit', () => {
        const body = form.querySelector('[data-md-application-summary]');
        if (!body) return;
        const fields = [...form.querySelectorAll('[data-md-application-field]')].filter(
          (field) => !field.disabled,
        );
        body.value = fields
          .map((field) => `${field.dataset.label}: ${field.value.trim() || 'Niet ingevuld'}`)
          .join('\n');
      });
    });
  }

  function initQuoteForms(root = document) {
    const query = getQuery();
    const item = query.get('item');
    root.querySelectorAll('[data-md-quote-product]').forEach((input) => {
      if (!input.value && item) input.value = item;
    });
    root.querySelectorAll('[data-md-quote-need]').forEach((input) => {
      if (!input.value && item) input.value = `Offerte voor ${item}`;
    });
    root.querySelectorAll('[data-md-quote-quantity]').forEach((input) => {
      const quantity = query.get('oplage');
      if (!input.value && quantity) input.value = quantity;
    });
    root.querySelectorAll('[data-md-quote-print]').forEach((select) => {
      if (select.value) return;
      const printChoice = query.get('properties[Bedrukking]');
      if (!printChoice) return;
      select.value = printChoice === 'Geen bedrukking' ? 'Nee' : 'Ja';
    });
    root.querySelectorAll('[data-md-quote-artwork]').forEach((fieldset) => {
      const artworkChoice = query.get('ontwerpkeuze');
      if (!artworkChoice) return;
      const mapped = artworkChoice === 'Ik lever een ontwerp aan'
        ? 'Ik lever mijn ontwerp later aan'
        : artworkChoice;
      const radio = [...fieldset.querySelectorAll('input[type="radio"]')].find(
        (input) => input.value === mapped,
      );
      if (radio) radio.checked = true;
    });
    root.querySelectorAll('[data-md-quote-body]').forEach((textarea) => {
      if (textarea.value) return;
      const configuration = [
        ['Uitvoering', query.get('md_product_choice')],
        ['Bedrukking', query.get('properties[Bedrukking]')],
        ['Ontwerpkeuze', query.get('ontwerpkeuze')],
      ].filter(([, value]) => value);
      if (configuration.length) {
        textarea.value = configuration.map(([label, value]) => `${label}: ${value}`).join('\n');
      }
    });
    root.querySelectorAll('[data-md-quote-source]').forEach((input) => {
      if (query.get('md_product_choice') || query.get('oplage')) input.value = 'Productconfigurator';
    });
  }

  function initStatusFocus(root = document) {
    const status = root.querySelector?.('[data-md-focus-status]');
    if (!status || status.dataset.focused === 'true') return;
    status.dataset.focused = 'true';
    window.requestAnimationFrame(() => status.focus({ preventScroll: true }));
  }

  function boot(root = document) {
    initNavigation(root);
    initCatalog(root);
    initCustomizer(root);
    initApplicationForms(root);
    initQuoteForms(root);
    initStatusFocus(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
